import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Calendar, Plus, Clock, User, FileText, AlertCircle, Settings } from 'lucide-react';
import { leavesApi, LeaveType, LeaveBalance, LeaveApplication as LeaveApp } from '../../services/api/leaves';
import { useAuthStore } from '../auth/authStore';
import toast from 'react-hot-toast';

interface LeaveApplicationProps {
  onApplicationSubmitted?: (application: LeaveApp) => void;
  showBalance?: boolean;
}

const LeaveApplication: React.FC<LeaveApplicationProps> = ({
  onApplicationSubmitted,
  showBalance = true,
}) => {
  const { user } = useAuthStore();
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [creatingLeaveTypes, setCreatingLeaveTypes] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    reason: '',
    isHalfDay: false,
  });

  // Fetch leave types and balances
  const fetchData = async () => {
    try {
      setLoading(true);
      const [balancesResponse, typesResponse] = await Promise.all([
        leavesApi.getLeaveBalance(),
        leavesApi.getLeaveTypes(),
      ]);

      console.log('🔍 API Responses:', { balancesResponse, typesResponse });
      console.log('🔍 Leave Types Array:', typesResponse);
      console.log('🔍 Leave Types Length:', typesResponse?.length);
      console.log('🔍 Leave Balances Array:', balancesResponse);
      console.log('🔍 Leave Balances Length:', balancesResponse?.length);

      // Debug individual balance objects
      if (balancesResponse && balancesResponse.length > 0) {
        console.log('🔍 First balance object:', balancesResponse[0]);
        console.log('🔍 Balance fields:', Object.keys(balancesResponse[0]));
      }
      setLeaveBalances(balancesResponse || []);
      setLeaveTypes(typesResponse || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLeaveBalances([]);
      setLeaveTypes([]);
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  // Function to create default leave types
  const createDefaultLeaveTypes = async () => {
    try {
      setCreatingLeaveTypes(true);
      console.log('🔄 Creating default leave types...');

      const response = await leavesApi.createDefaultLeaveTypes();
      console.log('✅ Default leave types created:', response);

      toast.success('Default leave types created successfully!');

      // Refresh leave types
      await fetchData();
    } catch (error: any) {
      console.error('❌ Error creating default leave types:', error);

      if (error.response?.status === 403) {
        toast.error('Only firm admin can create default leave types');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create default leave types');
      }
    } finally {
      setCreatingLeaveTypes(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Calculate leave days
  const calculateLeaveDays = () => {
    if (!formData.startDate || !formData.endDate) return 0;
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    
    if (end < start) return 0;
    
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return formData.isHalfDay ? 0.5 : diffDays;
  };

  // Get available balance for selected leave type
  const getAvailableBalance = () => {
    if (!formData.leaveTypeId) return null;

    console.log('🔍 Looking for balance with leaveTypeId:', formData.leaveTypeId);
    console.log('🔍 Available balances:', leaveBalances.map(b => ({
      id: b.id,
      leaveTypeId: b.leaveTypeId,
      available: b.available || b.availableDays,
      leaveTypeName: b.leaveType?.name
    })));

    // Try to find balance by leaveTypeId (could be _id or id, or populated object)
    const balance = leaveBalances.find(b => {
      // If leaveTypeId is a string (ObjectId)
      if (typeof b.leaveTypeId === 'string') {
        return b.leaveTypeId === formData.leaveTypeId;
      }
      // If leaveTypeId is populated (object)
      if (typeof b.leaveTypeId === 'object' && b.leaveTypeId) {
        return (b.leaveTypeId as any)._id === formData.leaveTypeId || (b.leaveTypeId as any).id === formData.leaveTypeId;
      }
      // If leaveType field exists
      if (b.leaveType) {
        return b.leaveType._id === formData.leaveTypeId || b.leaveType.id === formData.leaveTypeId;
      }
      return false;
    });
    console.log('🔍 Found balance:', balance);

    return balance ? (balance.available || balance.availableDays || 0) : 0;
  };

  // Validate form
  const validateForm = () => {
    if (!formData.leaveTypeId) {
      toast.error('Please select a leave type');
      return false;
    }
    
    if (!formData.startDate) {
      toast.error('Please select start date');
      return false;
    }
    
    if (!formData.endDate) {
      toast.error('Please select end date');
      return false;
    }
    
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error('End date cannot be before start date');
      return false;
    }
    
    if (!formData.reason.trim()) {
      toast.error('Please provide a reason for leave');
      return false;
    }
    
    const leaveDays = calculateLeaveDays();
    const availableBalance = getAvailableBalance();
    
    if (availableBalance !== null && leaveDays > availableBalance) {
      toast.error(`Insufficient leave balance. Available: ${availableBalance} days`);
      return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      
      const application = await leavesApi.applyForLeave({
        leaveTypeId: formData.leaveTypeId,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        reason: formData.reason.trim(),
        isHalfDay: formData.isHalfDay,
      });
      
      toast.success('Leave application submitted successfully!');
      
      // Reset form
      setFormData({
        leaveTypeId: '',
        startDate: '',
        endDate: '',
        reason: '',
        isHalfDay: false,
      });
      
      // Refresh balances
      await fetchData();
      
      // Notify parent component
      if (onApplicationSubmitted) {
        onApplicationSubmitted(application);
      }
    } catch (error: any) {
      console.error('Error submitting application:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit leave application';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const leaveDays = calculateLeaveDays();
  const availableBalance = getAvailableBalance();
  const selectedLeaveType = Array.isArray(leaveTypes) ? leaveTypes.find(type => type._id === formData.leaveTypeId) : null;

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Plus className="h-5 w-5" />
          <span>Apply for Leave</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Leave Type Selection */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Leave Type *
            </label>

            {loading ? (
              <div className="flex items-center justify-center p-4 border border-gray-200 rounded-md">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                <span className="text-sm text-gray-600">Loading leave types...</span>
              </div>
            ) : leaveTypes.length === 0 ? (
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                <div className="flex items-center space-x-2 text-amber-600 mb-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">No Leave Types Available</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  No leave types have been configured yet. A firm admin needs to create default leave types first.
                </p>
                {user?.roles?.includes('FIRM_ADMIN') ? (
                  <Button
                    type="button"
                    onClick={createDefaultLeaveTypes}
                    disabled={creatingLeaveTypes}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                  >
                    {creatingLeaveTypes ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Settings className="h-4 w-4 mr-2" />
                        Create Default Leave Types
                      </>
                    )}
                  </Button>
                ) : (
                  <p className="text-xs text-gray-500">
                    Please contact your firm admin to set up leave types.
                  </p>
                )}
              </div>
            ) : (
              <Select
                value={formData.leaveTypeId}
                onValueChange={(value) => handleInputChange('leaveTypeId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select leave type" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(leaveTypes) && leaveTypes.map((type) => (
                    <SelectItem key={type._id} value={type._id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Start Date *
              </label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                End Date *
              </label>
              <Input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                min={formData.startDate || new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          {/* Half Day Option */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="halfDay"
              checked={formData.isHalfDay}
              onChange={(e) => handleInputChange('isHalfDay', e.target.checked)}
            />
            <label htmlFor="halfDay" className="text-sm font-medium">
              Half Day Leave
            </label>
          </div>

          {/* Leave Summary */}
          {formData.startDate && formData.endDate && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Leave Duration:</span>
                <Badge variant="info">
                  {leaveDays} {leaveDays === 1 ? 'day' : 'days'}
                </Badge>
              </div>
              
              {availableBalance !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Available Balance:</span>
                  <Badge
                    variant={availableBalance >= leaveDays ? "success" : "error"}
                  >
                    {availableBalance} days
                  </Badge>
                </div>
              )}
              
              {availableBalance !== null && leaveDays > availableBalance && (
                <div className="flex items-center space-x-2 mt-2 text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">Insufficient leave balance</span>
                </div>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-sm font-medium mb-2 block">
              Reason for Leave *
            </label>
            <Textarea
              placeholder="Please provide a detailed reason for your leave..."
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Approval Info */}
          {selectedLeaveType?.requiresApproval && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 text-blue-800">
                <User className="h-4 w-4" />
                <span className="text-sm font-medium">
                  This leave type requires approval
                </span>
              </div>
              <p className="text-sm text-blue-600 mt-1">
                Your application will be sent to your manager for approval.
              </p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting || loading}
            className="w-full"
          >
            {submitting ? (
              <>
                <Clock className="h-4 w-4 animate-spin mr-2" />
                Submitting Application...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Submit Leave Application
              </>
            )}
          </Button>
        </form>

        {/* Leave Balances */}
        {showBalance && leaveBalances.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <h4 className="text-sm font-medium mb-3">Your Leave Balances</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {leaveBalances.map((balance) => (
                <div
                  key={balance.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: (() => {
                          if (balance.leaveType?.color) return balance.leaveType.color;
                          if (typeof balance.leaveTypeId === 'object' && balance.leaveTypeId && (balance.leaveTypeId as any).color) {
                            return (balance.leaveTypeId as any).color;
                          }
                          return '#gray';
                        })()
                      }}
                    />
                    <span className="text-sm font-medium">
                      {(() => {
                        // Try different ways to get the leave type name
                        if (balance.leaveType?.name) return balance.leaveType.name;
                        if (typeof balance.leaveTypeId === 'object' && balance.leaveTypeId && (balance.leaveTypeId as any).name) {
                          return (balance.leaveTypeId as any).name;
                        }
                        // Debug: log the balance object to see its structure
                        console.log('🔍 Balance object for name lookup:', balance);
                        return 'Unknown';
                      })()}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium">
                      {balance.available || balance.availableDays || 0} / {balance.totalAllotted || balance.totalDays || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Available / Total
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeaveApplication;
